import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory } from 'react-router';
import request from '../../../services/Request';
import Swal from 'sweetalert2';
import EditTechUserModal from './EditTechUserModal';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    borderRadius: 12,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  headerBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
}));

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
      {value === index && children}
    </div>
  );
}

export default function ManageTechUsers() {
  const classes = useStyles();
  const history = useHistory();
  const [tabValue, setTabValue] = useState(0);
  const [techUsers, setTechUsers] = useState([]);
  const [ticketUsers, setTicketUsers] = useState([]);
  const [subTicketUsers, setSubTicketUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Sub Ticket User creation dialog
  const [createSubUserOpen, setCreateSubUserOpen] = useState(false);
  const [createSubUserLoading, setCreateSubUserLoading] = useState(false);
  const [subUserForm, setSubUserForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    email2: '',
    parentTicketUserEmail: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [techResponse, ticketResponse, subTicketResponse] = await Promise.all([
        request({ method: 'get', url: '/users/getByRole/Tech' }),
        request({ method: 'get', url: '/users/getByRole/TicketUser' }),
        request({ method: 'get', url: '/users/getByRole/SubTicketUser' }),
      ]);
      setTechUsers(techResponse.data);
      setTicketUsers(ticketResponse.data);
      setSubTicketUsers(subTicketResponse.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch users',
      });
    }
    setLoading(false);
  };

  const handleCreateSubUser = async () => {
    const { firstname, lastname, email, parentTicketUserEmail } = subUserForm;
    if (!firstname || !lastname || !email || !parentTicketUserEmail) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'All fields are required' });
      return;
    }
    setCreateSubUserLoading(true);
    try {
      await request({
        method: 'post',
        url: '/users/createSubTicketUser',
        data: subUserForm,
      });
      Swal.fire({ icon: 'success', title: 'Success', text: 'Sub Ticket User created' });
      setCreateSubUserOpen(false);
      setSubUserForm({ firstname: '', lastname: '', email: '', email2: '', parentTicketUserEmail: '' });
      fetchUsers();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error?.response?.data?.message || 'Failed to create user' });
    }
    setCreateSubUserLoading(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleDelete = async (user) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${user.firstname} ${user.lastname}. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete!',
    });

    if (result.isConfirmed) {
      try {
        await request({
          method: 'delete',
          url: `/users/delete/${user._id}`,
        });

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'User has been deleted.',
        });

        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete user',
        });
      }
    }
  };

  const handleCreate = () => {
    history.push('/dashboard/signUp');
  };

  const getResponsibilitiesChips = (user) => {
    const chips = [];
    if (user.techResponsibilities?.hardware) {
      chips.push(
        <Chip
          key="hardware"
          label="Hardware"
          size="small"
          color="secondary"
          sx={{ mr: 0.5 }}
        />
      );
    }
    if (user.techResponsibilities?.software) {
      chips.push(
        <Chip
          key="software"
          label="Software"
          size="small"
          color="primary"
          sx={{ mr: 0.5 }}
        />
      );
    }
    if (chips.length === 0) {
      return <Typography variant="body2" color="textSecondary">None</Typography>;
    }
    return chips;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderTable = (users, showResponsibilities = false) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (users.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">No users found</Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              {showResponsibilities && <TableCell>Responsibilities</TableCell>}
              <TableCell>Created Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user._id} hover>
                <TableCell>
                  {user.firstname} {user.lastname}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    size="small"
                    color={user.role === 'Tech' ? 'primary' : 'default'}
                  />
                </TableCell>
                {showResponsibilities && (
                  <TableCell>{getResponsibilitiesChips(user)}</TableCell>
                )}
                <TableCell>{formatDate(user.createdAt)}</TableCell>
                <TableCell align="right">
                  {showResponsibilities && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(user)}
                      title="Edit Responsibilities"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(user)}
                    title="Delete User"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Paper className={classes.paper}>
        <Box className={classes.headerBox}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Tech & Ticket Users
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Create User
          </Button>
        </Box>

        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Tech Users (${techUsers.length})`} />
          <Tab label={`Ticket Users (${ticketUsers.length})`} />
          <Tab label={`Sub Ticket Users (${subTicketUsers.length})`} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {renderTable(techUsers, true)}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderTable(ticketUsers, false)}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateSubUserOpen(true)}
            >
              Add Sub User
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : subTicketUsers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary">No sub ticket users found</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Parent Group (TicketUser)</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subTicketUsers.map((u) => (
                    <TableRow key={u._id} hover>
                      <TableCell>{u.firstname} {u.lastname}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Chip label={u.referTo || '—'} size="small" color="info" variant="outlined" />
                      </TableCell>
                      <TableCell>{formatDate(u.createdAt)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="error" onClick={() => handleDelete(u)} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      <EditTechUserModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        user={selectedUser}
        onUpdate={fetchUsers}
      />

      {/* Create Sub Ticket User Dialog */}
      <Dialog open={createSubUserOpen} onClose={() => setCreateSubUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Sub Ticket User
          <IconButton onClick={() => setCreateSubUserOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                value={subUserForm.firstname}
                onChange={(e) => setSubUserForm({ ...subUserForm, firstname: e.target.value })}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={subUserForm.lastname}
                onChange={(e) => setSubUserForm({ ...subUserForm, lastname: e.target.value })}
              />
            </Box>
            <TextField
              label="Email"
              fullWidth
              type="email"
              autoComplete="new-email"
              value={subUserForm.email}
              onChange={(e) => setSubUserForm({ ...subUserForm, email: e.target.value })}
            />
            <TextField
              label="Email for Notifications (email2)"
              fullWidth
              type="email"
              autoComplete="new-email"
              placeholder="Email to receive alerts and notifications"
              value={subUserForm.email2}
              onChange={(e) => setSubUserForm({ ...subUserForm, email2: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Parent Ticket User (Group)</InputLabel>
              <Select
                value={subUserForm.parentTicketUserEmail}
                onChange={(e) => setSubUserForm({ ...subUserForm, parentTicketUserEmail: e.target.value })}
                label="Parent Ticket User (Group)"
              >
                {ticketUsers.map((tu) => (
                  <MenuItem key={tu._id} value={tu.email}>
                    {tu.firstname} {tu.lastname} ({tu.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSubUserOpen(false)} disabled={createSubUserLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSubUser} disabled={createSubUserLoading}>
            {createSubUserLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
